import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useSetting, formatDeadline } from '@/hooks/useSetting';
import { eventInfo } from '@/mocks/landing-data';

export default function AboutSection() {
  const [prizePool, setPrizePool] = useState<string | null>(null);
  const eventName = useSetting('event_name', eventInfo.name);
  const deadlineRaw = useSetting('registration_deadline', '');
  const displayDeadline = deadlineRaw ? formatDeadline(deadlineRaw) : eventInfo.registrationDeadline;

  useEffect(() => {
    async function fetchPrizePool() {
      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'prize_display_text')
          .maybeSingle();

        if (!error && data?.value) {
          setPrizePool(data.value);
        }
      } catch {
        // silently fall back to mock data
      }
    }

    fetchPrizePool();
  }, []);

  const displayPrizePool = prizePool ? `₹${prizePool}` : eventInfo.totalPrizePool;

  return (
    <section id="about" className="py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-3">
            About the Event
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
            What is <span className="text-primary-600">{eventName}</span>?
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20medical%20hackathon%20event%20with%20diverse%20students%20collaborating%20around%20laptops%20in%20a%20bright%20modern%20conference%20hall%2C%20white%20walls%2C%20natural%20lighting%2C%20professional%20healthcare%20innovation%20atmosphere%2C%20clean%20minimalist%20aesthetic%2C%20warm%20and%20energetic%20vibe%2C%20no%20visible%20faces%20clearly%2C%20abstract%20environment&width=800&height=600&seq=sona-clinnova-about-1&orientation=landscape"
              alt="Medical hackathon participants collaborating"
              className="w-full h-auto rounded-2xl"
            />
          </div>
          <div className="space-y-6">
            <p className="text-foreground-700 leading-relaxed text-base">
              <strong>{eventName}</strong> is a premier medical innovation challenge hosted by <strong>Sona College of Technology, Salem</strong> in association with the <strong>Indian Medical Association (IMA), Salem</strong>. Over 2 days, 300+ participants from across the country will come together to build technology solutions that address critical gaps in healthcare delivery, diagnostics, and patient care.
            </p>
            <p className="text-foreground-700 leading-relaxed text-base">
              Hosted at the state-of-the-art campus of <strong>Sona College of Technology, Salem</strong>, this event bridges the gap between medical expertise and technological innovation. Participants get access to mentorship from leading clinicians, AI researchers, and health-tech entrepreneurs.
            </p>
            <div className="grid grid-cols-2 gap-4 pt-4">
              {[
                { icon: 'ri-calendar-check-line', label: 'Registration Deadline', value: displayDeadline },
                { icon: 'ri-map-pin-line', label: 'Venue', value: 'Sona College of Technology, Salem' },
                { icon: 'ri-award-line', label: 'Prize Pool', value: displayPrizePool },
                { icon: 'ri-group-line', label: 'Team Size', value: '3-4 Members' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3 p-4 rounded-xl bg-background-100">
                  <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-primary-600 text-sm`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500 mb-0.5">{item.label}</div>
                    <div className="text-sm font-semibold text-foreground-900">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}